import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowUpRight } from 'lucide-react';
import { FAMILIES, PRODUCTS, ATMOSPHERE } from '../data/products';
import Breadcrumbs from '../components/Breadcrumbs';
import ChapterBreak from '../components/ChapterBreak';
import { Reveal } from '../components/Reveal';

export default function Collections() {
  const capsule = PRODUCTS.filter((p) => p.family === 'capsule');
  const studio = PRODUCTS.filter((p) => p.family === 'studio');
  return (
    <main data-testid="collections-page" className="bg-white text-[#111]">
      <Helmet>
        <title>Collections | MODULAROOM</title>
        <meta name="description" content="Two collections of luxury modular homes. Capsule Suites for iconic architecture and Studio Suites for prefabricated homes, modular resort rooms and modular offices." />
        <link rel="canonical" href="https://modularoom.com/collections" />
      </Helmet>
      <section className="pt-40 md:pt-56 pb-16 max-w-[1440px] mx-auto px-6 md:px-12">
        <Breadcrumbs items={[{label:'Home', to:'/'}, {label:'Products', to:'/products'}, {label:'Collections'}]} />
        <h1 className="mt-14 font-display text-6xl md:text-7xl lg:text-8xl tracking-tight leading-[0.95] max-w-4xl">Two collections.<br/><span className="italic text-black/40">One obsession with detail.</span></h1>
      </section>

      <ChapterBreak src={ATMOSPHERE.aboutHeadline} chapter="The Collection" minHeight="60svh" />

      {[
        { f: FAMILIES.capsule, items: capsule, tone: 'light' },
        { f: FAMILIES.studio,  items: studio,  tone: 'dark' },
      ].map(({ f, items, tone }, idx) => (
        <section key={f.id} className={`${tone === 'dark' ? 'bg-[#111] text-white' : 'bg-white text-[#111]'} py-32 md:py-48`}>
          <div className="max-w-[1440px] mx-auto px-6 md:px-12">
            <div className={`grid grid-cols-1 md:grid-cols-12 gap-10 items-end ${idx % 2 === 1 ? '' : ''}`}>
              <div className="md:col-span-7 relative aspect-[16/10] overflow-hidden">
                <img src={f.image} alt={f.name} className="absolute inset-0 w-full h-full object-cover" />
              </div>
              <div className="md:col-span-5">
                <div className="text-[10px] tracking-[0.32em] uppercase text-[#C7A36A]">{f.tagline}</div>
                <h2 className="mt-4 font-display text-5xl md:text-6xl tracking-tight leading-[0.98]">{f.name}.</h2>
                <p className={`mt-6 max-w-md text-[15px] leading-relaxed ${tone === 'dark' ? 'text-white/70' : 'text-black/65'}`}>{f.description}</p>
                <div className={`mt-8 text-[10px] tracking-[0.32em] uppercase ${tone === 'dark' ? 'text-white/45' : 'text-black/45'}`}>Models in this collection</div>
                <div className="mt-5 grid grid-cols-2 gap-3">
                  {items.map((p) => (
                    <Link key={p.slug} to={`/products/${p.slug}`} className={`group flex items-center justify-between rounded-none border px-4 py-3 text-sm transition-colors ${tone === 'dark' ? 'border-white/15 hover:border-[#C7A36A] hover:text-[#C7A36A]' : 'border-black/15 hover:border-[#C7A36A] hover:text-[#C7A36A]'}`}>
                      <span className="font-display text-lg">{p.name}</span>
                      <span className="font-mono-luxe text-[11px] opacity-60">{p.specs.carpetArea}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}
    </main>
  );
}
